import { useQuery } from "@tanstack/react-query"

/**
 * Open-Meteo 일별 예보 — 최고/최저기온, 평균습도만 필요.
 * Docs: https://open-meteo.com/en/docs
 *
 * 제한:
 *  - 최대 7일 범위 (과거 6일 + 오늘 or 오늘 + 미래 16일 중 7일만 사용)
 *  - API 키 불필요, CORS 허용
 *  - 상업적 사용 시 유료 플랜 필요 (MVP 단계는 비상업 무료로 진행)
 */

const OPEN_METEO_URL = "https://api.open-meteo.com/v1/forecast"

export interface WeatherDaily {
  /** ISO yyyy-MM-dd */
  date: string
  /** 섭씨 최고기온 */
  tempMax: number | null
  /** 섭씨 최저기온 */
  tempMin: number | null
  /** 일평균 상대습도 (%) */
  humidity: number | null
  /** WMO weather code (아이콘 매핑용) */
  weatherCode: number | null
}

export interface UseWeatherQueryParams {
  latitude: number
  longitude: number
  /**
   * 오늘 기준 과거 N일 포함 (0~92). Open-Meteo past_days 파라미터에 매핑.
   */
  pastDays?: number
  /** 오늘 포함 미래 N일 (1~16). Open-Meteo forecast_days 파라미터에 매핑. */
  forecastDays?: number
  /** 훅 활성화 여부 (위치 조회 완료 후 켜기용) */
  enabled?: boolean
}

interface OpenMeteoResponse {
  daily?: {
    time?: string[]
    temperature_2m_max?: Array<number | null>
    temperature_2m_min?: Array<number | null>
    relative_humidity_2m_mean?: Array<number | null>
    weather_code?: Array<number | null>
  }
}

/** localStorage 캐시 TTL — 날씨는 시간 단위로 갱신되면 충분 */
const WEATHER_CACHE_TTL_MS = 1000 * 60 * 60 // 1시간
const WEATHER_CACHE_PREFIX = "weather-cache:"

interface WeatherCacheEntry {
  ts: number
  data: WeatherDaily[]
}

function makeCacheKey(params: {
  latitude: number
  longitude: number
  pastDays: number
  forecastDays: number
}): string {
  // 동일 근방 위치는 같은 캐시 사용 → 소수점 2자리(약 1km 해상도)로 반올림
  const lat = params.latitude.toFixed(2)
  const lon = params.longitude.toFixed(2)
  return `${WEATHER_CACHE_PREFIX}${lat}:${lon}:${params.pastDays}:${params.forecastDays}`
}

function readCache(key: string): WeatherDaily[] | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw) as WeatherCacheEntry
    if (Date.now() - entry.ts > WEATHER_CACHE_TTL_MS) {
      localStorage.removeItem(key)
      return null
    }
    return entry.data
  } catch {
    return null
  }
}

function writeCache(key: string, data: WeatherDaily[]): void {
  try {
    const entry: WeatherCacheEntry = { ts: Date.now(), data }
    localStorage.setItem(key, JSON.stringify(entry))
  } catch {
    // quota exceeded 등 무시 — 캐시는 optional
  }
}

async function fetchWeather(params: UseWeatherQueryParams): Promise<WeatherDaily[]> {
  const pastDays = clamp(params.pastDays ?? 0, 0, 92)
  const forecastDays = clamp(params.forecastDays ?? 7, 1, 16)

  const cacheKey = makeCacheKey({
    latitude: params.latitude,
    longitude: params.longitude,
    pastDays,
    forecastDays,
  })

  // 1) localStorage 캐시 히트 — TTL 내면 API 호출 생략
  const cached = readCache(cacheKey)
  if (cached) return cached

  // 2) 네트워크 호출
  const search = new URLSearchParams({
    latitude: String(params.latitude),
    longitude: String(params.longitude),
    daily: "temperature_2m_max,temperature_2m_min,relative_humidity_2m_mean,weather_code",
    timezone: "auto",
    past_days: String(pastDays),
    forecast_days: String(forecastDays),
  })

  const res = await fetch(`${OPEN_METEO_URL}?${search.toString()}`)
  if (!res.ok) throw new Error(`Weather fetch failed: ${res.status}`)

  const json = (await res.json()) as OpenMeteoResponse
  const time = json.daily?.time ?? []
  const tMax = json.daily?.temperature_2m_max ?? []
  const tMin = json.daily?.temperature_2m_min ?? []
  const hum = json.daily?.relative_humidity_2m_mean ?? []
  const code = json.daily?.weather_code ?? []

  const data = time.map((date, i) => ({
    date,
    tempMax: tMax[i] ?? null,
    tempMin: tMin[i] ?? null,
    humidity: hum[i] ?? null,
    weatherCode: code[i] ?? null,
  }))

  // 3) 성공 시 localStorage에 저장
  writeCache(cacheKey, data)

  return data
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * 지정 위치의 일별 날씨(최고/최저기온, 평균습도) 예보.
 * 최대 7일 범위 (pastDays + forecastDays ≤ 7이 되도록 내부에서 clamp).
 */
export function useWeatherQuery(params: UseWeatherQueryParams) {
  const { latitude, longitude, pastDays = 0, forecastDays = 7, enabled = true } = params

  return useQuery<WeatherDaily[], Error>({
    queryKey: ["weather", latitude, longitude, pastDays, forecastDays],
    queryFn: () => fetchWeather({ latitude, longitude, pastDays, forecastDays }),
    enabled: enabled && Number.isFinite(latitude) && Number.isFinite(longitude),
    staleTime: 1000 * 60 * 30, // 30분 — 날씨는 자주 변경되지 않음
    gcTime: 1000 * 60 * 60, // 1시간
    retry: 1,
  })
}
